import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { FiLayers, FiArrowLeft } from 'react-icons/fi';
import { fetchActivitySubcoverageByIdRequest } from '../../store/actions/dataActions';
import './AdminActivitySubcoverageDetail.css';

const AdminActivitySubcoverageDetail = () => {
    const { subcoverageId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedActivitySubcoverage } = useSelector((state) => state.data);

    useEffect(() => {
        if (subcoverageId) {
            dispatch(fetchActivitySubcoverageByIdRequest(subcoverageId));
        }
    }, [dispatch, subcoverageId]);

    const { data, loading, error } = selectedActivitySubcoverage;
    const subcoverage = data?.subcoverage || {};
    const reference = data?.reference || {};
    const partner = data?.partner || {};

    const renderValue = (value, fallback = 'N/A') => {
        if (value === undefined || value === null || value === '') {
            return fallback;
        }
        return value;
    };

    return (
        <div className="dashboard-page admin-activity-subcoverage-detail">
            <Card className="mb-3">
                <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="page-title">
                            <FiLayers className="me-2" />
                            Détail sous-couverture activité
                        </h2>
                        <p className="text-muted mb-0">
                            Informations détaillées de la sous-couverture sélectionnée.
                        </p>
                    </div>
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        <FiArrowLeft className="me-1" /> Retour
                    </Button>
                </Card.Body>
            </Card>

            {loading && (
                <div className="text-center my-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            )}

            {!loading && error && (
                <Alert variant="danger">{error}</Alert>
            )}

            {!loading && !error && data && (
                <>
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Sous-couverture</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div className="detail-label">Code couverture</div>
                                    <div className="detail-value">{renderValue(subcoverage.coverage_code)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Identifiant</div>
                                    <div className="detail-value">{renderValue(subcoverage.sub_coverage_id)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Type</div>
                                    <div className="detail-value">
                                        <Badge bg="info" text="dark" className="text-capitalize">
                                            {renderValue(subcoverage.trigger_type)}
                                        </Badge>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Unité</div>
                                    <div className="detail-value">{renderValue(subcoverage.unit)}</div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div className="detail-label">Début</div>
                                    <div className="detail-value">{renderValue(subcoverage.sub_coverage_start_date)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Fin</div>
                                    <div className="detail-value">{renderValue(subcoverage.sub_coverage_end_date)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Heure & jour de référence</div>
                                    <div className="detail-value">
                                        {renderValue(subcoverage.timing_hour, '0')}h / Jour {renderValue(subcoverage.timing_day, 'N/A')}
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div className="detail-label">Ville</div>
                                    <div className="detail-value">{renderValue(subcoverage.city)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Pays</div>
                                    <div className="detail-value">{renderValue(subcoverage.country)}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Adresse activité</div>
                                    <div className="detail-value">{renderValue(subcoverage.activity_address)}</div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <div className="detail-label">SIV Maintien / Annulation</div>
                                    <div className="detail-value">
                                        {renderValue(subcoverage.siv_ratio_maintained)} / {renderValue(subcoverage.siv_ratio_cancelled)}
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Trigger bas</div>
                                    <div className="detail-value">
                                        {renderValue(subcoverage.trigger_low)}
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="detail-label">Source données</div>
                                    <div className="detail-value">{renderValue(subcoverage.data_source)}</div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Card className="h-100">
                                <Card.Header>
                                    <h5 className="mb-0">Référence</h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="detail-label">Nom</div>
                                    <div className="detail-value">{renderValue(reference.name)}</div>
                                    <div className="detail-label mt-3">Code</div>
                                    <div className="detail-value">{renderValue(reference.reference_code)}</div>
                                    <div className="detail-label mt-3">Catégorie</div>
                                    <div className="detail-value">{renderValue(reference.category)}</div>
                                    <div className="detail-label mt-3">Visible</div>
                                    <div className="detail-value">{reference.is_visible_reference ? 'Oui' : 'Non'}</div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="h-100">
                                <Card.Header>
                                    <h5 className="mb-0">Partenaire</h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="detail-label">Entreprise</div>
                                    <div className="detail-value">{renderValue(partner.company_name, `${renderValue(partner.first_name, '')} ${renderValue(partner.last_name, '')}`)}</div>
                                    <div className="detail-label mt-3">Email</div>
                                    <div className="detail-value">{renderValue(partner.email)}</div>
                                    <div className="detail-label mt-3">Téléphone</div>
                                    <div className="detail-value">{renderValue(partner.phone_number)}</div>
                                    <div className="detail-label mt-3">Adresse</div>
                                    <div className="detail-value">{renderValue(partner.user_address)}</div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
};

export default AdminActivitySubcoverageDetail;

